import DeleteIcon from '@assets/icons/delete.svg';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  Animated,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { AnimatedSkeletonLoader } from './animated-skeleton-loader';
import { CLEAR_FILTERS_BUTTON_SIZE } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { PillButtonType } from './pill-button';
import { ScrollView } from 'react-native-gesture-handler';

interface Props {
  buttons: PillButtonType[];
  containerStyle?: StyleProp<ViewStyle>;
  disableClear?: boolean;
  loading?: boolean;
  renderPillButton: (button: PillButtonType) => React.ReactNode;
}

const NUMBER_OF_PILLS = 3; // Default number of pills to show when loading

export const FilterPills: React.FC<Props> = ({
  buttons,
  containerStyle,
  disableClear,
  loading,
  renderPillButton,
}) => {
  const [styles, theme] = useStyles();
  const clearButtonAnimation = useRef(new Animated.Value(0)).current;

  // Match Animated values to the number of filter buttons
  const marginAnimations = useMemo(() => {
    return Array.from({ length: buttons.length }, () => new Animated.Value(1));
  }, [buttons.length]);

  useEffect(() => {
    if (buttons.some((button) => button.active)) {
      Animated.timing(clearButtonAnimation, {
        duration: 200,
        toValue: 1,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(clearButtonAnimation, {
        duration: 200,
        toValue: 0,
        useNativeDriver: false,
      }).start();
    }
  }, [buttons, clearButtonAnimation]);

  useEffect(() => {
    // Animate the margin of each button based on whether it is collapsed
    buttons.forEach((button, index) => {
      if (button.collapsed) {
        Animated.timing(marginAnimations[index], {
          duration: 200,
          toValue: 0,
          useNativeDriver: false,
        }).start();
      } else {
        Animated.timing(marginAnimations[index], {
          duration: 200,
          toValue: 1,
          useNativeDriver: false,
        }).start();
      }
    });
  }, [buttons, marginAnimations]);

  const clearFilters = () => {
    buttons.forEach((button) => button.onClear?.());
    // Reset all the margin animations to value 1
    marginAnimations.forEach((animation) => {
      Animated.timing(animation, {
        duration: 200,
        toValue: 1,
        useNativeDriver: false,
      }).start();
    });
  };

  const clearButtonWidth = clearButtonAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 32],
  });

  const clearButtonMargin = clearButtonAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 8],
  });

  const clearButtonOpacity = clearButtonAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  const clearButtonTranslateX = clearButtonAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [-60, 0],
  });

  if (loading) {
    return (
      <View
        accessibilityState={{ busy: true }}
        style={[styles.base, styles.skeletonPill, containerStyle]}
      >
        {Array.from({ length: NUMBER_OF_PILLS }).map((_, index) => (
          <AnimatedSkeletonLoader
            borderRadius={36}
            height={32}
            key={index}
            width={53}
          />
        ))}
      </View>
    );
  }

  return (
    <ScrollView
      horizontal
      nestedScrollEnabled
      showsHorizontalScrollIndicator={false}
    >
      <View style={[styles.base, containerStyle]}>
        {disableClear ? null : (
          <Animated.View
            key="clear-button"
            style={[
              {
                marginRight: clearButtonMargin,
                width: clearButtonWidth,
              },
            ]}
          >
            <Animated.View
              style={[
                {
                  opacity: clearButtonOpacity,
                  transform: [{ translateX: clearButtonTranslateX }],
                },
              ]}
            >
              <TouchableOpacity
                onPress={() => clearFilters()}
                style={styles.clearButton}
                testID="clear-filters"
              >
                <DeleteIcon fill={theme.colors.primary} height={12} />
              </TouchableOpacity>
            </Animated.View>
          </Animated.View>
        )}
        {buttons.map((button, index) => {
          const margin = marginAnimations[index].interpolate({
            inputRange: [0, 1],
            outputRange: [0, 8],
          });
          button.style = { marginRight: margin };
          return renderPillButton(button);
        })}
      </View>
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    display: 'flex',
    flexDirection: 'row',
    marginHorizontal: 16,
    paddingVertical: 8,
  },
  clearButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: CLEAR_FILTERS_BUTTON_SIZE,
    height: CLEAR_FILTERS_BUTTON_SIZE,
    justifyContent: 'center',
    width: CLEAR_FILTERS_BUTTON_SIZE,
  },
  skeletonPill: {
    gap: 8,
    marginBottom: 8, // spacing to match the paddingVertical when loaded
  },
}));
